import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { formatMarkdownContent } from '../utils/markdownFormatter';

interface CMSContent {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  slug: string;
  content: string;
  is_active: boolean;
  version: number;
}

interface CMSContentPageProps {
  fetchContent: () => Promise<CMSContent>;
}

export const CMSContentPage: React.FC<CMSContentPageProps> = ({ fetchContent }) => {
  const [content, setContent] = useState<CMSContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const data = await fetchContent();
        setContent(data);
      } catch (err) {
        setError('Error cargando el contenido');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [fetchContent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-salbox-primary mx-auto mb-4"></div>
          <p className="text-salbox-neutral-600 font-lufga">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 font-lufga mb-4">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center text-salbox-primary hover:text-salbox-primary-600 font-lufga-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Regresar al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-salbox-neutral-600 font-lufga">No se encontró el contenido</p>
          <Link
            to="/"
            className="inline-flex items-center text-salbox-primary hover:text-salbox-primary-600 font-lufga-medium mt-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Regresar al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-salbox-primary hover:text-salbox-primary-600 font-lufga-medium mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Regresar al inicio
          </Link>
          <h1 className="text-3xl font-lufga-semibold text-salbox-primary">{content.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="prose prose-lg max-w-none font-lufga text-salbox-neutral-700 leading-relaxed">
              {formatMarkdownContent({
                content: content.content,
                baseTextClass: "text-base font-lufga text-salbox-neutral-700 mb-4 leading-relaxed",
                h1Class: "text-2xl font-lufga-semibold mb-6 text-salbox-neutral-900",
                h2Class: "text-xl font-lufga-semibold mb-4 text-salbox-neutral-900 mt-8",
                h3Class: "text-lg font-lufga-semibold mb-3 text-salbox-neutral-800 mt-6",
                listItemClass: "text-base font-lufga mb-2 text-salbox-neutral-700 leading-relaxed ml-6",
                boldClass: "font-lufga-semibold text-salbox-neutral-900"
              })}
            </div>
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-salbox-neutral-500 font-lufga">
                Última actualización: {new Date(content.updated_at).toLocaleDateString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};